import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Download, ArrowLeft, Edit, Printer } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_24eb0a6d-9d2f-4d94-a017-bc81aa56f600/artifacts/r7716m4i_Sparkle%20Grant%20Solutions%20-%201.png';

const InvoiceView = () => {
  const { invoiceNumber } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const printRef = useRef();

  useEffect(() => {
    fetchInvoice();
  }, [invoiceNumber]);

  const fetchInvoice = async () => {
    try {
      const response = await axios.get(`${API}/invoices/${invoiceNumber}`);
      setInvoice(response.data);
    } catch (error) {
      console.error('Error fetching invoice:', error);
      toast({
        title: 'Error',
        description: 'Failed to load invoice',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Invoice not found</h2>
          <Button onClick={() => navigate('/')}>Back to Dashboard</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-yellow-50 py-8">
      {/* Action Buttons - Hidden when printing */}
      <div className="max-w-4xl mx-auto px-6 mb-6 no-print">
        <div className="flex justify-between items-center">
          <Button variant="outline" onClick={() => navigate('/')} data-testid="back-to-dashboard-btn">
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to Dashboard
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate(`/edit/${invoiceNumber}`)} data-testid="edit-invoice-btn">
              <Edit className="mr-2 h-4 w-4" /> Edit
            </Button>
            <Button onClick={handlePrint} className="bg-gradient-to-r from-purple-600 to-pink-600" data-testid="print-invoice-btn">
              <Printer className="mr-2 h-4 w-4" /> Print / Download PDF
            </Button>
          </div>
        </div>
      </div>

      {/* Invoice Content */}
      <div ref={printRef} className="max-w-4xl mx-auto px-6">
        <Card className="bg-white shadow-xl overflow-hidden">
          <div className="p-8 md:p-12">
            {/* Header with Logo and Title */}
            <div className="flex justify-between items-start mb-8 pb-6 border-b-2 border-gray-200">
              <div>
                <img src={LOGO_URL} alt="Sparkle Grant Solutions" className="h-20 w-auto mb-4" />
                <h1 className="text-3xl font-bold text-gray-900">Sparkle Grant Solutions, LLC</h1>
                <p className="text-gray-600 mt-1">Grant Research & Writing Services</p>
                <div className="mt-3 text-sm text-gray-600 space-y-1">
                  <p className="flex items-center gap-2">
                    <span className="font-semibold">Phone:</span> 866-707-7272
                  </p>
                  <p className="flex items-center gap-2">
                    <span className="font-semibold">Email:</span> info@sparklegrantsolutions.com
                  </p>
                  <p className="flex items-center gap-2 ml-14">sparklegrantsolutions@gmail.com</p>
                </div>
              </div>
              <div className="text-right">
                <div className="inline-block bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-2 rounded-lg mb-2">
                  <h2 className="text-2xl font-bold">INVOICE</h2>
                </div>
                <p className="text-xl font-semibold text-gray-900">#{invoice.invoice_number}</p>
                <p className="text-gray-600 mt-1">{invoice.date}</p>
              </div>
            </div>

            {/* Bill To Section */}
            <div className="mb-8">
              <h3 className="text-sm font-semibold text-gray-600 uppercase mb-2">Bill To:</h3>
              <p className="text-lg font-semibold text-gray-900">{invoice.client_name}</p>
              {invoice.client_address && (
                <p className="text-gray-600 mt-1">{invoice.client_address}</p>
              )}
            </div>

            {/* Services Section */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b-2 border-gray-200">
                Services Provided
              </h3>
              <div className="space-y-3">
                {invoice.line_items.map((item, index) => (
                  <div key={index} className="flex items-start">
                    <span className="inline-block w-6 h-6 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 text-white text-center text-sm font-semibold mr-3 flex-shrink-0 leading-6">
                      {index + 1}
                    </span>
                    <p className="text-gray-700 flex-1">{item.description}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Payment Breakdown Section */}
            <div className="mb-8 bg-gradient-to-r from-purple-50 to-pink-50 p-6 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment Breakdown</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center pb-2 border-b border-gray-300">
                  <span className="text-gray-700 font-medium">Total Amount:</span>
                  <span className="text-xl font-bold text-gray-900">${invoice.total_amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
                <div className="flex justify-between items-center pb-2 border-b border-gray-300">
                  <span className="text-gray-700 font-medium">Deposit (Due to Start Project):</span>
                  <span className="text-lg font-semibold text-green-600">-${invoice.deposit_amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
                <div className="flex justify-between items-center pt-2">
                  <span className="text-gray-900 font-bold text-lg">Remainder (Due on Completion):</span>
                  <span className="text-2xl font-bold text-purple-600">${invoice.remainder_amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
              </div>
            </div>

            {/* Payment Terms */}
            <div className="mb-8 p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded">
              <h3 className="text-sm font-semibold text-gray-900 uppercase mb-2">Payment Terms:</h3>
              <p className="text-gray-700">{invoice.payment_terms}</p>
            </div>

            {/* Payment Method */}
            <div className="mb-8">
              <h3 className="text-sm font-semibold text-gray-600 uppercase mb-2">Payment Method:</h3>
              <p className="text-gray-900 font-medium">{invoice.payment_method}</p>
            </div>

            {/* Notes */}
            {invoice.notes && (
              <div className="mb-8 p-4 bg-gray-50 rounded-lg">
                <h3 className="text-sm font-semibold text-gray-600 uppercase mb-2">Notes:</h3>
                <p className="text-gray-700 text-sm leading-relaxed">{invoice.notes}</p>
              </div>
            )}

            {/* Footer */}
            <div className="mt-12 pt-6 border-t-2 border-gray-200 text-center">
              <p className="text-gray-600 text-sm">
                Thank you for your business!
              </p>
              <p className="text-gray-500 text-xs mt-3">
                For questions regarding this invoice, please contact Sparkle Grant Solutions, LLC
              </p>
              <div className="text-gray-500 text-xs mt-2 space-y-1">
                <p>Phone: 866-707-7272</p>
                <p>Email: info@sparklegrantsolutions.com | sparklegrantsolutions@gmail.com</p>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default InvoiceView;
